// Home animations of the mockup (V6) that Alpha GSAP presets (config/gsap.yaml) do not cover:
// method steps path drawn on scroll, tennis ball thrown into the padel card, Google rating counter,
// colour wipe when switching Particuliers / Professionnels.
import ballSrc from '../../images/home/tennis-ball.png'

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
const SVG_NS = 'http://www.w3.org/2000/svg'
const HALF = 0.5
const CUBIC = 3
const FRAME_MS = 16.7
const MAX_FRAME_STEP = 2

// mockup values (Jardin Ecoute v6)
const STEPS = {
  minNodes: 2,
  startAt: 0.82, // progress starts when the grid top reaches 82% of the viewport
  extraRange: 0.42,
  minSpan: 60,
  spanDivider: 2.2,
  firstLead: 0.6,
  nodeScale: 0.6,
  ringOpacity: 0.8,
  ringGrow: 0.8,
  textDelay: 0.35,
  textShift: 18
}

const BALL = {
  bounceOffset: 4,
  startOffset: 20,
  maxDrop: 200,
  dropRatio: 0.45,
  speedX: 5.5,
  gravity: 0.55,
  restitution: 0.6,
  rolling: 0.9975,
  minBounce: 1.2,
  bounceDrag: 0.92,
  exitGapTolerance: 10,
  exitBehindImage: 60,
  exitOutside: 80,
  halfTurn: 180,
  threshold: 0.5
}

const COUNTER = {
  duration: 1400,
  threshold: 0.12
}

const clamp01 = (value) => Math.max(0, Math.min(1, value))
const easeOutCubic = (value) => 1 - (1 - value) ** CUBIC

// === METHOD: dashed track + path drawn through the step nodes, nodes and texts revealed as the path reaches them
function initSteps(grid) {
  const steps = [...grid.querySelectorAll('.icon-block')]
  const nodes = steps.map((step) => step.querySelector('.home-step-icon'))
  const texts = steps.map((step) => step.querySelector('.inner-row-block'))
  if (nodes.length < STEPS.minNodes || nodes.includes(null) || texts.includes(null)) {
    return
  }

  const svg = document.createElementNS(SVG_NS, 'svg')
  svg.setAttribute('class', 'home-steps-path')
  svg.setAttribute('aria-hidden', 'true')
  svg.innerHTML = '<path fill="none" stroke="#C9D8C2" stroke-width="2" stroke-dasharray="4 8"></path>' +
    '<path fill="none" stroke="#05662f" stroke-width="2.5" stroke-linecap="round"></path>' +
    '<circle r="5" fill="#86bc25" opacity="0"></circle>'
  const [track, path, dot] = svg.children
  grid.prepend(svg)
  grid.classList.add('is-drawn')

  const rings = nodes.map((node) => {
    const ring = document.createElement('span')
    ring.className = 'home-step-ring'
    node.append(ring)

    return ring
  })

  let length = 0
  let cumulated = [0]

  const layout = () => {
    const gridRect = grid.getBoundingClientRect()
    const points = nodes.map((node) => {
      const r = node.getBoundingClientRect()

      return [r.left - gridRect.left + r.width * HALF, r.top - gridRect.top + r.height * HALF]
    })
    const d = points.map((p, i) => `${i ? 'L' : 'M'}${p[0]} ${p[1]}`).join(' ')
    track.setAttribute('d', d)
    path.setAttribute('d', d)
    length = path.getTotalLength()
    path.style.strokeDasharray = length
    cumulated = [0]
    for (let k = 1; k < points.length; k++) {
      cumulated.push(cumulated[k - 1] + Math.hypot(points[k][0] - points[k - 1][0], points[k][1] - points[k - 1][1]))
    }
  }

  const update = () => {
    const r = grid.getBoundingClientRect()
    const vh = window.innerHeight
    const progress = clamp01((vh * STEPS.startAt - r.top) / (r.height + vh * STEPS.extraRange))
    const span = Math.max(STEPS.minSpan, length / (nodes.length * STEPS.spanDivider))
    const target = progress * (length + span)

    path.style.strokeDashoffset = Math.max(0, length - target)
    if (progress > 0 && target < length) {
      const pt = path.getPointAtLength(target)
      dot.setAttribute('cx', pt.x)
      dot.setAttribute('cy', pt.y)
      dot.setAttribute('opacity', '1')
    } else {
      dot.setAttribute('opacity', '0')
    }

    nodes.forEach((node, i) => {
      const lead = i === 0 ? span * STEPS.firstLead : 0
      const k = clamp01((target - cumulated[i] + lead) / span)
      const eased = easeOutCubic(k)
      node.style.scale = String(STEPS.nodeScale + (1 - STEPS.nodeScale) * eased)
      node.style.opacity = String(eased)
      rings[i].style.opacity = String(k > 0 && k < 1 ? (1 - k) * STEPS.ringOpacity : 0)
      rings[i].style.scale = String(1 + k * STEPS.ringGrow)
      const tk = clamp01((k - STEPS.textDelay) / (1 - STEPS.textDelay))
      texts[i].style.opacity = String(tk)
      texts[i].style.translate = `0 ${((1 - tk) * STEPS.textShift).toFixed(1)}px`
    })
  }

  layout()
  update()
  window.addEventListener('scroll', update, {
    passive: true
  })
  window.addEventListener('resize', () => {
    layout()
    update()
  })
}

// === PADEL: a tennis ball thrown in from the left, bouncing on the bottom of the card then rolling behind the photo
function dropBall(card, ball) {
  const size = ball.offsetWidth
  const floor = card.clientHeight - size + BALL.bounceOffset
  const next = card.nextElementSibling
  const cardRect = card.getBoundingClientRect()
  const nextRect = next ? next.getBoundingClientRect() : null
  const exitX = nextRect && nextRect.left > cardRect.right - BALL.exitGapTolerance
    ? card.clientWidth + (nextRect.left - cardRect.right) + BALL.exitBehindImage
    : card.clientWidth + BALL.exitOutside
  let x = -size - BALL.startOffset
  let y = -Math.min(BALL.maxDrop, card.clientHeight * BALL.dropRatio)
  let vx = BALL.speedX
  let vy = 0
  let last = null

  ball.style.opacity = '1'
  const step = (now) => {
    if (last === null) {
      last = now
    }

    const dt = Math.min(MAX_FRAME_STEP, (now - last) / FRAME_MS)
    last = now
    vy += BALL.gravity * dt
    x += vx * dt
    y += vy * dt
    if (y >= floor) {
      y = floor
      if (vy > BALL.minBounce) {
        vy = -vy * BALL.restitution
        vx *= BALL.bounceDrag
      } else {
        vy = 0
      }
    }

    if (y === floor) {
      vx *= BALL.rolling ** dt
    }

    // rotation = distance / radius (rolling without slipping)
    const rotation = x / (size * HALF) * BALL.halfTurn / Math.PI
    ball.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) rotate(${rotation.toFixed(1)}deg)`
    if (x < exitX) {
      requestAnimationFrame(step)
    } else {
      ball.style.opacity = '0'
    }
  }

  requestAnimationFrame(step)
}

function initBall(card) {
  const ball = document.createElement('img')
  ball.className = 'home-padel-ball'
  ball.src = ballSrc
  ball.alt = ''
  ball.setAttribute('aria-hidden', 'true')
  card.prepend(ball)

  const observer = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting)) {
      observer.disconnect()
      dropBall(card, ball)
    }
  }, {
    threshold: BALL.threshold
  })
  observer.observe(card)
}

// === REVIEWS: the rating counts up from 0 when the card shows up ("4,7")
function initCounter(el) {
  const raw = el.textContent.trim()
  const target = parseFloat(raw.replace(',', '.'))
  if (Number.isNaN(target)) {
    return
  }

  const decimals = (raw.split(',')[1] || '').length
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((e) => e.isIntersecting)) {
      return
    }

    observer.disconnect()
    const start = performance.now()
    const tick = (now) => {
      const p = Math.min(1, (now - start) / COUNTER.duration)
      el.textContent = (target * easeOutCubic(p)).toFixed(decimals).replace('.', ',')
      if (p < 1) {
        requestAnimationFrame(tick)
      }
    }

    requestAnimationFrame(tick)
  }, {
    threshold: COUNTER.threshold
  })
  observer.observe(el)
}

if (!reducedMotion) {
  document.querySelectorAll('.home-steps-grid').forEach(initSteps)
  document.querySelectorAll('.home-padel-content').forEach(initBall)
  // pages "GAPE (sections)" too: Google rating counters
  document.querySelectorAll('.home-reviews-value, .gp-score-value, .gp-badge-note').forEach(initCounter)
}
